import { cn } from '@1trading/react';
import type { ColumnDef } from '@tanstack/react-table';
import { DateCell, UuidCell } from '@/components/data-table/cells';
import { StatusChip } from '@/components/ui/status-chip';
import { ALERT_STATE_CHIP_VARIANTS, ALERT_STATE_LABELS, formatScore, getScoreClass } from '@/features/abuse/abuse-meta';
import type { AbuseAlertState, AbuseSubjectActivity, AbuseSubjectType } from '@/services/api/abuse';

export interface ScoringThresholds {
  threshold: number;
  escalationThreshold: number;
}

const SubjectIdCell = ({ subjectType, subjectId }: { subjectType: AbuseSubjectType; subjectId: string }) =>
  subjectType === 'ip' ? <span className="font-mono text-xs">{subjectId}</span> : <UuidCell value={subjectId} />;
SubjectIdCell.displayName = 'SubjectIdCell';

// Uncoloured until the summary supplies the thresholds, rather than guessing them
const ScoreCell = ({ score, thresholds }: { score: number; thresholds: ScoringThresholds | null }) => (
  <span className={cn('tabular-nums font-medium', thresholds && getScoreClass(score, thresholds.threshold, thresholds.escalationThreshold))}>{formatScore(score)}</span>
);
ScoreCell.displayName = 'ScoreCell';

const EMPTY_CELL = <span className="text-muted-foreground">—</span>;

const OptionalDateCell = ({ value }: { value: string | undefined }) => (value ? <DateCell value={value} /> : EMPTY_CELL);
OptionalDateCell.displayName = 'OptionalDateCell';

// Idle is the resting state (and the only one in shadow mode), so only an active state earns a chip
const AlertStateCell = ({ alertState }: { alertState: AbuseAlertState }) =>
  alertState === 'idle' ? (
    EMPTY_CELL
  ) : (
    <StatusChip className="px-2 py-0.5 text-xs" variant={ALERT_STATE_CHIP_VARIANTS[alertState]}>
      {ALERT_STATE_LABELS[alertState]}
    </StatusChip>
  );
AlertStateCell.displayName = 'AlertStateCell';

const CountCell = ({ count, isCapped }: { count: number; isCapped: boolean }) => <span className="tabular-nums">{isCapped ? `${count}+` : count}</span>;
CountCell.displayName = 'CountCell';

// Sorting is disabled throughout: the server ranks subjects and orders events, client sorting would only reorder a page
export const subjectColumns = (thresholds: ScoringThresholds | null): ColumnDef<AbuseSubjectActivity, unknown>[] => [
  { accessorKey: 'subjectId', header: 'Subject', enableSorting: false, cell: ({ row }) => <SubjectIdCell {...row.original} /> },
  { accessorKey: 'peakScore', header: 'Peak', enableSorting: false, cell: ({ row }) => <ScoreCell score={row.original.peakScore} thresholds={thresholds} /> },
  { accessorKey: 'score', header: 'Now', enableSorting: false, cell: ({ row }) => <ScoreCell score={row.original.score} thresholds={thresholds} /> },
  {
    accessorKey: 'alertState',
    header: 'State',
    enableSorting: false,
    cell: ({ row }) => <AlertStateCell alertState={row.original.alertState} />,
  },
  { accessorKey: 'eventCount', header: 'Events', enableSorting: false, cell: ({ row }) => <CountCell count={row.original.eventCount} isCapped={row.original.isActivityCapped} /> },
  {
    accessorKey: 'linkedCount',
    header: 'Linked',
    enableSorting: false,
    cell: ({ row }) => <CountCell count={row.original.linkedCount} isCapped={row.original.isActivityCapped} />,
  },
  { accessorKey: 'triggeredAt', header: 'Last triggered', enableSorting: false, cell: ({ row }) => <OptionalDateCell value={row.original.triggeredAt} /> },
];
